import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import ReactMarkdown from "react-markdown";
import ConfirmDialog from "@/components/ConfirmDialog";
import PromptEditor from "@/components/PromptEditor";
import PublishTargetModal from "@/components/PublishTargetModal";
import { useAuth } from "@/lib/auth";
import {
  Sparkles,
  Trash2,
  Loader2,
  Edit3,
  Eye,
  Save,
  Send,
  CheckCircle2,
  EyeOff,
  X,
  ChevronRight,
} from "lucide-react";

const StatusBadge = ({ b }) => {
  if (b.status === "generating") {
    return (
      <span className="badge-neutral !bg-purple-50 !text-purple-700 font-semibold flex items-center gap-1 text-[10px]">
        <Loader2 size={10} className="animate-spin" /> GENERAZIONE
      </span>
    );
  }
  if (b.status === "error") {
    return <span className="badge-neutral !bg-red-50 !text-red-700 font-semibold text-[10px]">ERRORE</span>;
  }
  if (b.status === "published") {
    return <span className="badge-pos flex items-center gap-1 text-[10px]"><CheckCircle2 size={10} /> Pubblicato</span>;
  }
  return <span className="badge-neutral !bg-amber-50 !text-amber-700 font-semibold text-[10px]">BOZZA</span>;
};

export default function AdminBriefing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [autoPublish, setAutoPublish] = useState(false);
  const [sendNotif, setSendNotif] = useState(true);
  const [briefingType, setBriefingType] = useState("technical");
  const [targetType, setTargetType] = useState("all");
  const [targetClientIds, setTargetClientIds] = useState([]);
  // Publish modal state
  const [publishModal, setPublishModal] = useState(null); // { item }
  // Modal state
  const [openId, setOpenId] = useState(null);
  const [modalMode, setModalMode] = useState("view"); // "view" | "edit" | "preview"
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [confirmState, setConfirmState] = useState(null);

  const load = async () => {
    const { data } = await api.get("/briefings");
    setItems(data);
  };
  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.filter((u) => u.role === "cliente"));
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    load();
    loadUsers();
  }, []);

  useEffect(() => {
    const hasGenerating = items.some((b) => b.status === "generating");
    if (!hasGenerating) return;
    const t = setInterval(() => { load(); }, 5000);
    return () => clearInterval(t);
  }, [items]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/briefings/generate", {
        auto_publish: autoPublish,
        send_notification: autoPublish && sendNotif,
        briefing_type: briefingType,
        target_type: targetType,
        target_client_ids: targetType === "clients" ? targetClientIds : [],
      });
      toast.success("Generazione avviata — richiede 2-4 minuti. Ti avviseremo quando è pronto.");
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore";
      toast.error(typeof msg === "string" ? msg : "Errore");
    } finally { setBusy(false); }
  };

  const toggleClientTarget = (id) => {
    setTargetClientIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openModal = (b, mode = "view") => {
    setOpenId(b.id);
    setModalMode(mode);
    setEditTitle(b.title || "");
    setEditContent(b.content_markdown || "");
  };
  const closeModal = () => {
    setOpenId(null);
    setModalMode("view");
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/briefings/${openId}`, { title: editTitle, content_markdown: editContent });
      toast.success("Modifiche salvate");
      await load();
      setModalMode("view");
    } catch (e) { toast.error("Errore salvataggio"); }
  };

  const publishNow = (b) => {
    setPublishModal({
      item: b,
      onConfirm: async ({ target, company_ids, client_ids, send_notification, send_email, email_subject, email_message, email_client_ids, email_company_ids, email_group_id }) => {
        try {
          await api.post(`/briefings/${b.id}/publish`, {
            send_notification,
            send_email,
            email_subject,
            email_message,
            target_type: target === "companies" ? "companies" : target === "users" ? "clients" : "all",
            target_company_ids: target === "companies" ? company_ids : [],
            target_client_ids: target === "users" ? client_ids : [],
            email_client_ids: email_client_ids || [],
            email_company_ids: email_company_ids || [],
            email_group_id: email_group_id || null,
          });
          toast.success(
            "Briefing pubblicato" +
              (target === "companies"
                ? ` a ${company_ids.length} aziend${company_ids.length === 1 ? "a" : "e"}`
                : target === "users"
                ? ` a ${client_ids.length} utent${client_ids.length === 1 ? "e" : "i"}`
                : " a tutti i clienti")
          );
          setPublishModal(null);
          await load();
          if (openId === b.id) closeModal();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore pubblicazione");
        }
      },
    });
  };

  const unpublish = (b) => {
    setConfirmState({
      title: "Nascondere il briefing ai clienti?",
      message: `"${b.title}" tornerà in bozza e non sarà più visibile nell'area cliente.`,
      confirmLabel: "Nascondi ai clienti",
      variant: "warning",
      onConfirm: async () => {
        try {
          await api.post(`/briefings/${b.id}/unpublish`);
          toast.success("Briefing nascosto ai clienti (bozza)");
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore");
        }
      },
    });
  };

  const del = (b) => {
    setConfirmState({
      title: "Eliminare il briefing?",
      message: `"${b.title}" verrà rimosso definitivamente.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/briefings/${b.id}`);
          toast.success("Briefing eliminato");
          if (openId === b.id) closeModal();
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  const openBriefing = openId ? items.find((b) => b.id === openId) : null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Gestione Report Mercati Energetici</h1>
        <p className="text-sm text-slate-500 mt-1">
          Report AI professionale sui mercati energetici europei. La generazione avviene in background e richiede 2-4 minuti — riceverai un aggiornamento automatico.
        </p>
      </div>

      <div className="card-elevated p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center shadow-lg shadow-purple-500/40 flex-shrink-0">
            <Sparkles className="text-white" size={26} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold text-lg text-slate-900">Genera nuovo briefing</div>
            <div className="text-sm text-slate-500">L&apos;AI produce una bozza — potrai modificarla e poi pubblicarla ai clienti.</div>
          </div>
          <button onClick={generate} disabled={busy} className="btn-brand flex items-center gap-2" data-testid="briefing-generate">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {busy ? "Generazione…" : "Genera con AI"}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 block mb-2">Tipologia report</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className={`cursor-pointer border rounded-xl p-3 transition-colors ${briefingType === "technical" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="briefing-type-technical">
                <div className="flex items-center gap-2">
                  <input type="radio" name="briefing-type" value="technical" checked={briefingType === "technical"} onChange={() => setBriefingType("technical")} />
                  <div className="font-semibold text-sm">Report tecnico</div>
                </div>
                <div className="text-xs text-slate-500 mt-1 ml-6">Analisi da analista senior con terminologia trader (spark spread, contango, storage %).</div>
              </label>
              <label className={`cursor-pointer border rounded-xl p-3 transition-colors ${briefingType === "simplified" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="briefing-type-simplified">
                <div className="flex items-center gap-2">
                  <input type="radio" name="briefing-type" value="simplified" checked={briefingType === "simplified"} onChange={() => setBriefingType("simplified")} />
                  <div className="font-semibold text-sm">Report sintetico e discorsivo</div>
                </div>
                <div className="text-xs text-slate-500 mt-1 ml-6">Linguaggio accessibile per lettori non tecnici, focus divulgativo.</div>
              </label>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 block mb-2">Visibilità</label>
            <div className="flex flex-wrap gap-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="briefing-target-all">
                <input type="radio" name="briefing-target" value="all" checked={targetType === "all"} onChange={() => setTargetType("all")} />
                <span>Tutti i clienti</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="briefing-target-clients">
                <input type="radio" name="briefing-target" value="clients" checked={targetType === "clients"} onChange={() => setTargetType("clients")} />
                <span>Solo clienti selezionati</span>
              </label>
            </div>
            {targetType === "clients" && (
              <div className="mt-3 border border-slate-200 rounded-xl p-3 max-h-52 overflow-y-auto bg-slate-50/40">
                {users.length === 0 && <div className="text-xs text-slate-400">Nessun cliente disponibile.</div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-white cursor-pointer" data-testid={`briefing-target-user-${u.id}`}>
                      <input type="checkbox" checked={targetClientIds.includes(u.id)} onChange={() => toggleClientTarget(u.id)} />
                      <span className="truncate">
                        <b>{u.company_name || u.full_name}</b> <span className="text-slate-400">· {u.email}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {targetClientIds.length} cliente{targetClientIds.length !== 1 ? "i" : ""} selezionat{targetClientIds.length !== 1 ? "i" : "o"}.
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-5 text-sm pt-2 border-t border-slate-100">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-auto-publish">
              <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} />
              <span>Pubblica automaticamente (salta revisione)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={sendNotif} onChange={(e) => setSendNotif(e.target.checked)} />
              <span>Invia notifica ai destinatari alla pubblicazione</span>
            </label>
          </div>
        </div>
      </div>

      {/* Prompt editors visibili solo agli admin */}
      {isAdmin && (
        <div className="space-y-3">
          <PromptEditor promptKey={briefingType === "simplified" ? "briefing_simplified" : "briefing_technical"} />
        </div>
      )}

      {/* Compact history list */}
      <div className="card-elevated overflow-hidden" data-testid="briefing-history">
        <div className="px-5 py-3 border-b border-slate-100 font-display font-semibold text-slate-700 flex items-center justify-between">
          <span>Storico briefing</span>
          <span className="text-xs font-normal text-slate-400">{items.length} elementi</span>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Nessun briefing ancora generato.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((b) => {
              const isDraft = b.status !== "published" && b.status !== "generating" && b.status !== "error";
              return (
                <li
                  key={b.id}
                  className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50/50 cursor-pointer group"
                  onClick={() => openModal(b, "view")}
                  data-testid={`briefing-row-${b.id}`}
                >
                  <StatusBadge b={b} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-semibold text-slate-900 text-sm truncate">{b.title}</span>
                      {b.briefing_type === "simplified" && (
                        <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">SINTETICO</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{fmtDateTime(b.generated_at)}</div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    {b.status !== "generating" && b.status !== "error" && (
                      <button
                        onClick={() => openModal(b, "edit")}
                        className="p-1.5 text-slate-500 hover:text-blue-600"
                        title="Modifica"
                        data-testid={`briefing-edit-${b.id}`}
                      >
                        <Edit3 size={14} />
                      </button>
                    )}
                    {isDraft && (
                      <button
                        onClick={() => publishNow(b)}
                        className="p-1.5 text-emerald-600 hover:text-emerald-700"
                        title="Pubblica"
                        data-testid={`briefing-publish-${b.id}`}
                      >
                        <Send size={14} />
                      </button>
                    )}
                    {b.status === "published" && (
                      <button
                        onClick={() => unpublish(b)}
                        className="px-2 py-1 text-xs font-semibold text-amber-700 hover:text-white hover:bg-amber-600 border border-amber-300 rounded-md flex items-center gap-1 transition-colors"
                        title="Nascondi ai clienti — riporta in bozza"
                        data-testid={`briefing-unpublish-${b.id}`}
                      >
                        <EyeOff size={12} /> Nascondi
                      </button>
                    )}
                    <button
                      onClick={() => del(b)}
                      className="p-1.5 text-red-500 hover:text-red-700"
                      title="Elimina"
                      data-testid={`briefing-delete-${b.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Modal — view / edit briefing */}
      {openBriefing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeModal} data-testid="briefing-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge b={openBriefing} />
                  <span className="text-xs text-slate-500">{fmtDateTime(openBriefing.generated_at)}</span>
                </div>
                {modalMode === "edit" ? (
                  <input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="mt-2 w-full font-display font-bold text-lg text-slate-900 px-3 py-1.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none"
                    data-testid="briefing-modal-edit-title"
                  />
                ) : (
                  <h3 className="mt-1 font-display font-bold text-lg text-slate-900">{openBriefing.title}</h3>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {modalMode === "view" && openBriefing.status !== "generating" && openBriefing.status !== "error" && (
                  <button onClick={() => setModalMode("edit")} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:border-blue-500 hover:text-blue-600 flex items-center gap-1.5" data-testid="briefing-modal-edit-btn">
                    <Edit3 size={14} /> Modifica
                  </button>
                )}
                {modalMode === "edit" && (
                  <>
                    <button onClick={() => setModalMode(modalMode === "preview" ? "edit" : "preview")} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:border-blue-500 hover:text-blue-600 flex items-center gap-1.5">
                      <Eye size={14} /> Anteprima
                    </button>
                    <button onClick={saveEdit} className="text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5" data-testid="briefing-modal-save">
                      <Save size={14} /> Salva
                    </button>
                  </>
                )}
                {modalMode === "preview" && (
                  <button onClick={() => setModalMode("edit")} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:border-blue-500 flex items-center gap-1.5">
                    <Edit3 size={14} /> Modifica
                  </button>
                )}
                {openBriefing.status !== "published" && openBriefing.status !== "generating" && openBriefing.status !== "error" && (
                  <button onClick={() => publishNow(openBriefing)} className="btn-brand text-sm !py-1.5 !px-3 flex items-center gap-1.5" data-testid="briefing-modal-publish">
                    <Send size={14} /> Pubblica
                  </button>
                )}
                <button onClick={closeModal} className="p-2 text-slate-400 hover:text-slate-600" data-testid="briefing-modal-close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {openBriefing.status === "generating" ? (
                <div className="flex items-center gap-3 text-sm text-purple-700 bg-purple-50 rounded-xl px-4 py-3">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Claude Sonnet sta redigendo il report professionale — richiede tipicamente 2-4 minuti.</span>
                </div>
              ) : openBriefing.status === "error" ? (
                <div className="text-sm text-red-700 bg-red-50 rounded-xl px-4 py-3">
                  Errore durante la generazione: {openBriefing.error_message || "generico"}
                </div>
              ) : modalMode === "edit" ? (
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={20}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 outline-none text-sm font-mono leading-relaxed"
                  data-testid="briefing-modal-edit-content"
                />
              ) : (
                <div className="prose prose-slate prose-sm max-w-none [&_h2]:font-display [&_h2]:text-blue-700 [&_h2]:text-base [&_h2]:mt-4 [&_h2]:mb-1">
                  <ReactMarkdown>{modalMode === "preview" ? editContent : (openBriefing.content_markdown || "_(vuoto)_")}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <PublishTargetModal
        open={!!publishModal}
        title="Pubblica briefing"
        message={
          publishModal?.item
            ? `"${publishModal.item.title}" — scegli il pubblico destinatario.`
            : undefined
        }
        defaultEmailSubject={publishModal?.item ? `Nuovo Report Mercati Energetici: ${publishModal.item.title}` : ""}
        defaultEmailMessage={publishModal?.item ? publishModal.item.title : ""}
        onCancel={() => setPublishModal(null)}
        onConfirm={publishModal?.onConfirm || (() => {})}
      />
    </div>
  );
}
